import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { GlassCard, KpiTile } from '../components/shared';
import LoadingSpinner from '../components/LoadingSpinner';
import axios from 'axios';
import { AlertTriangle, CheckCircle, DollarSign, TrendingUp, Download, Filter, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SLACredits = () => {
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Filters
  const [filters, setFilters] = useState({
    station_id: '',
    breach_type: '',
    status: '',
    from_date: '',
    to_date: ''
  });
  
  // Modal states
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showManualCreditModal, setShowManualCreditModal] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // Get JWT token from localStorage
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [creditsRes, statsRes, reconRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/governance/credits?${params.toString()}`, { headers }),
        axios.get(`${BACKEND_URL}/api/governance/credits/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/governance/reconciliation/daily?date=${selectedDate}`, { headers })
      ]);

      setCredits(creditsRes.data.credits || []);
      setStats(statsRes.data);
      setReconciliation(reconRes.data);
    } catch (error) {
      console.error('Error fetching SLA credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDispute = async (creditId, reason, evidence) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.put(`${BACKEND_URL}/api/governance/credits/${creditId}/dispute`, {
        disputed_by: 'station_owner_1', // TODO: Get from auth context
        dispute_reason: reason,
        evidence_urls: evidence
      }, { headers });
      
      alert('Credit disputed successfully');
      setShowDisputeModal(false);
      fetchData();
    } catch (error) {
      console.error('Error disputing credit:', error);
      alert('Failed to dispute credit');
    }
  };

  const handleManualCredit = async (data) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${BACKEND_URL}/api/governance/credits`, {
        ...data,
        posted_by: 'admin' // TODO: Get from auth context
      }, { headers });
      
      alert('Manual credit posted successfully');
      setShowManualCreditModal(false);
      fetchData();
    } catch (error) {
      console.error('Error posting manual credit:', error);
      alert('Failed to post manual credit');
    }
  };

  const downloadStatement = async (stationId, month) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${BACKEND_URL}/api/governance/statement/${stationId}?month=${month}`,
        { responseType: 'blob', headers }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${stationId}_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading statement:', error);
      alert('Failed to download statement');
    }
  };

  const runBreachDetection = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${BACKEND_URL}/api/governance/breach-detection/run?date=${selectedDate}`, {}, { headers });
      alert(`Breach detection complete: ${response.data.summary.total_breaches} breaches found, ${response.data.summary.inserted_count} credits posted`);
      fetchData();
    } catch (error) {
      console.error('Error running breach detection:', error);
      alert('Failed to run breach detection');
    }
  };

  const getBadgeColor = (type) => {
    const colors = {
      uptime: 'bg-red-100 text-red-700',
      mttr: 'bg-orange-100 text-orange-700',
      mtta: 'bg-yellow-100 text-yellow-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || colors.other;
  };

  const getStatusBadge = (status) => {
    const colors = {
      posted: 'bg-green-100 text-green-700',
      disputed: 'bg-red-100 text-red-700',
      resolved: 'bg-blue-100 text-blue-700',
      waived: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || colors.posted;
  };

  return (
    <>
      <SEOHead 
        title="SLA Credits - Automated Governance"
        description="Automated SLA credit calculation for uptime, MTTR, and MTTA breaches. Zero disputes with transparent credit tracking and reconciliation."
        path="/planning/sla-credits"
        type="WebApplication"
        noIndex={true}
      />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SLA Credits & Governance</h1>
          <p className="text-sm text-gray-600">Uptime, MTTR, MTTA breach management</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              const stationId = prompt('Enter Station ID:');
              const currentMonth = new Date().toISOString().slice(0, 7);
              if (stationId) downloadStatement(stationId, currentMonth);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download size={18} />
            Download Statement
          </button>
          <button
            onClick={runBreachDetection}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <AlertTriangle size={18} />
            Run Breach Detection
          </button>
          <button
            onClick={() => setShowManualCreditModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={18} />
            Manual Credit
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <KpiTile
            title="Total Credits MTD"
            value={`₹${stats.total_amount?.toLocaleString() || 0}`}
            subtitle={`${stats.total_credits || 0} breaches`}
            icon={<DollarSign size={18} />}
            status="purple"
          />
          <KpiTile
            title="Uptime Breaches"
            value={`₹${stats.by_type?.uptime?.amount?.toLocaleString() || 0}`}
            subtitle={`${stats.by_type?.uptime?.count || 0} incidents`}
            icon={<AlertTriangle size={18} />}
            status="red"
          />
          <KpiTile
            title="MTTR Breaches"
            value={`₹${stats.by_type?.mttr?.amount?.toLocaleString() || 0}`}
            subtitle={`${stats.by_type?.mttr?.count || 0} incidents`}
            icon={<TrendingUp size={18} />}
            status="warning"
          />
          <KpiTile
            title="MTTA Breaches"
            value={`₹${stats.by_type?.mtta?.amount?.toLocaleString() || 0}`}
            subtitle={`${stats.by_type?.mtta?.count || 0} incidents`}
            icon={<CheckCircle size={18} />}
            status="amber"
          />
          <KpiTile
            title="Other Credits"
            value={`₹${stats.by_type?.other?.amount?.toLocaleString() || 0}`}
            subtitle={`${stats.by_type?.other?.count || 0} items`}
            icon={<DollarSign size={18} />}
            status="gray"
          />
        </div>
      )}

      {/* Daily Reconciliation */}
      {reconciliation && (
        <GlassCard className="p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg">Daily Reconciliation - {reconciliation.date}</h3>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          
          <div className="grid grid-cols-4 gap-6">
            <div>
              <div className="text-sm text-gray-600">Gross Revenue</div>
              <div className="text-2xl font-bold text-green-600">₹{reconciliation.gross_revenue?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Credits</div>
              <div className="text-2xl font-bold text-red-600">₹{reconciliation.total_credits?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Net Revenue</div>
              <div className="text-2xl font-bold text-blue-600">₹{reconciliation.net_revenue?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Credits/Gross Ratio</div>
              <div className={`text-2xl font-bold ${reconciliation.alert_triggered ? 'text-red-600' : 'text-gray-900'}`}>
                {reconciliation.credits_to_gross_ratio}%
              </div>
              {reconciliation.alert_triggered && (
                <div className="text-xs text-red-600 mt-1">⚠️ Above threshold (2.5%)</div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="text-sm font-medium text-red-700">Uptime</div>
              <div className="text-lg font-bold">₹{reconciliation.uptime_credits?.toLocaleString()}</div>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <div className="text-sm font-medium text-orange-700">MTTR</div>
              <div className="text-lg font-bold">₹{reconciliation.mttr_credits?.toLocaleString()}</div>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <div className="text-sm font-medium text-yellow-700">MTTA</div>
              <div className="text-lg font-bold">₹{reconciliation.mtta_credits?.toLocaleString()}</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700">Other</div>
              <div className="text-lg font-bold">₹{reconciliation.other_credits?.toLocaleString()}</div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Filters */}
      <GlassCard className="p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={18} />
          <span className="font-semibold">Filters</span>
        </div>
        <div className="grid grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Station ID</label>
            <input
              type="text"
              value={filters.station_id}
              onChange={(e) => setFilters({...filters, station_id: e.target.value})}
              placeholder="e.g. STN-001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Breach Type</label>
            <select
              value={filters.breach_type}
              onChange={(e) => setFilters({...filters, breach_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Types</option>
              <option value="uptime">Uptime</option>
              <option value="mttr">MTTR</option>
              <option value="mtta">MTTA</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Status</option>
              <option value="posted">Posted</option>
              <option value="disputed">Disputed</option>
              <option value="resolved">Resolved</option>
              <option value="waived">Waived</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters({...filters, from_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters({...filters, to_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </GlassCard>

      {/* Credits Table */}
      <GlassCard className="p-6">
        <h3 className="font-semibold text-lg mb-4">SLA Credits</h3>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credit ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Station</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Breach Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Threshold</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {credits.map((credit) => (
                  <tr key={credit.credit_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{credit.credit_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{credit.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{credit.station_name}</div>
                      <div className="text-xs text-gray-500">{credit.station_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getBadgeColor(credit.breach_type)}`}>
                        {credit.breach_type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{credit.threshold_value}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{credit.actual_value?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-red-600">₹{credit.credit_amount_inr?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(credit.status)}`}>
                        {credit.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {credit.status === 'posted' && (
                        <button
                          onClick={() => {
                            setSelectedCredit(credit);
                            setShowDisputeModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Dispute
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {credits.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No SLA credits found for the selected filters
              </div>
            )}
          </div>
        )}
      </GlassCard>

      {/* Dispute Modal */}
      {showDisputeModal && selectedCredit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Dispute Credit</h2>
            <p className="text-sm text-gray-600 mb-4">Credit ID: {selectedCredit.credit_id}</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const reason = e.target.reason.value;
              const evidence = e.target.evidence.value.split(',').map(s => s.trim()).filter(Boolean);
              handleDispute(selectedCredit.credit_id, reason, evidence);
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Dispute Reason</label>
                <textarea
                  name="reason"
                  rows="3"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Explain why you're disputing this credit..."
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Evidence URLs (comma-separated)</label>
                <input
                  type="text"
                  name="evidence"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://example.com/proof1, https://example.com/proof2"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Submit Dispute
                </button>
                <button
                  type="button"
                  onClick={() => setShowDisputeModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Credit Modal */}
      {showManualCreditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Post Manual Credit</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleManualCredit({
                station_id: e.target.station_id.value,
                date: e.target.date.value,
                breach_type: e.target.breach_type.value,
                credit_amount_inr: parseFloat(e.target.amount.value),
                reason: e.target.reason.value,
                calculation_details: { manual_entry: true, reason: e.target.reason.value }
              });
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Station ID</label>
                <input
                  type="text"
                  name="station_id"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="STN-001"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  name="date"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Breach Type</label>
                <select
                  name="breach_type"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="uptime">Uptime</option>
                  <option value="mttr">MTTR</option>
                  <option value="mtta">MTTA</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Credit Amount (₹)</label>
                <input
                  type="number"
                  name="amount"
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="5000.00"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <textarea
                  name="reason"
                  rows="3"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Explain the reason for manual credit..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Post Credit
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualCreditModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default SLACredits;
